import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  makeStyles,
  TablePagination,
  CardActionArea,
} from "@material-ui/core";
import { DateRange as DateRangeIcon } from "@material-ui/icons";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.2s ease-in-out",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[4],
    },
  },
  cardContent: {
    height: 320,
    display: "flex",
    flexDirection: "column",
    position: "relative",
    paddingBottom: "60px",
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: "bold",
  },
  snippet: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  dateWrapper: {
    position: "absolute",
    bottom: theme.spacing(2),
    left: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    color: theme.palette.text.secondary,
  },
  dateIcon: {
    marginRight: theme.spacing(1),
    fontSize: "1.2rem",
  },
  pagination: {
    marginTop: theme.spacing(4),
    display: "flex",
    justifyContent: "center",
  },
}));

const Litvar = ({ rsID }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [rowsPerPage] = useState(10);

  const truncateText = (text, maxLength = 300) => {
    if (!text) return "";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  const handleCardClick = (pmid) => {
    const url = `https://www.ncbi.nlm.nih.gov/research/litvar2/publication/${pmid}?variant=litvar@${rsID}%23%23&query=${rsID}`;
    window.open(url, '_blank');
  };

  const fetchData = async (pageNumber) => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://www.ncbi.nlm.nih.gov/research/litvar2-api/search/?variant=litvar@${rsID}%23%23&filters=%7B%7D&page=${pageNumber + 1}&sort=date%20desc`
      );
      const result = await response.json();
      setData(result.results);
      setTotalPages(result.total_pages || 0);
    } catch (error) {
      console.error("Litvar2 API error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (rsID) {
      fetchData(page);
    }
  }, [rsID, page]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box p={4}>
        <Typography variant="body1">
          {t("No Litvar² data available for this variant")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={4}>
      <Grid container spacing={3}>
        {data.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card className={classes.card}>
              <CardActionArea onClick={() => handleCardClick(item.pmid)}>
                <CardContent className={classes.cardContent}>
                  <Typography variant="h6" className={classes.title}>
                    {truncateText(item.title)}
                  </Typography>
                  {item.snippets && item.snippets[0] && (
                    <Typography variant="body2" className={classes.snippet}>
                      {truncateText(item.snippets[0].snip_text)}
                    </Typography>
                  )}
                  <Box className={classes.dateWrapper}>
                    <DateRangeIcon className={classes.dateIcon} />
                    <Typography variant="body2">
                      {formatDate(item.date)}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Box className={classes.pagination}>
        <TablePagination
          component="div"
          count={totalPages * rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10]}
          onRowsPerPageChange={() => {}}
        />
      </Box>
    </Box>
  );
};

export default Litvar; 